/**
 * reopenVisit — clears edcalltime on a CallLog (server-side) so the
 * rep can resume an accidentally-closed visit. Also flips the local
 * row back to 'in_progress' for immediate UI feedback.
 * Depends on the parent visit having synced.
 */

import type { WriteOp, SyncResult } from '../types';
import { STORES, put, getAll } from '../../utils/indexedDB';
import { emit } from '../../utils/eventLog';
import {
  jsonHeaders,
  classifyResponse,
  networkErrorResult,
  resolveCallLogUrl,
} from '../httpHelpers';

export interface ReopenVisitInput {
  visitLocalId: string;
}

export interface ReopenVisitResponse {
  calllogid: number;
}

export const reopenVisitOp: WriteOp<ReopenVisitInput, ReopenVisitInput, ReopenVisitResponse> = {
  kind: 'reopenVisit',

  async applyLocal(input) {
    const rows = (await getAll(STORES.VISIT_TASK_SESSIONS)) as Array<Record<string, unknown>>;
    const match = rows.find((r) => r.uuid === input.visitLocalId);
    if (!match) return;
    const updated = { ...match, status: 'in_progress' };
    delete (updated as Record<string, unknown>).endTime;
    delete (updated as Record<string, unknown>).checkOutTime;
    await put(STORES.VISIT_TASK_SESSIONS, updated);

    // Event stream (#3530). Reopening UNDOES a store_checked_out, and until now
    // it said so nowhere: the ledger kept the checkout, so a fresh device
    // replaying the day through reconstruct_active_day rebuilt the visit as
    // closed and the rep lost the work they had resumed. Emitted from
    // applyLocal, the single chokepoint that runs once per reopen whatever the
    // UI entry point. `time_edited` carries it rather than a new event type,
    // matching how a deleted clock window is recorded — the server validates
    // event_type against a fixed list and rejects anything unknown.
    try {
      await emit({
        event_type: 'time_edited',
        store_id: (match.storeId as number) ?? null,
        project_id: (match.projectId as number) ?? null,
        calllog_id: (match.callLogId as number) ?? null,
        payload: { field: 'visit_reopened', local_id: input.visitLocalId },
      });
    } catch {
      /* event emit is best-effort — it must never break the reopen */
    }
  },

  serialize(input, localId) {
    return {
      action: 'reopenVisit',
      endpoint: '/api/projects/calllog/{calllogid}/',
      method: 'PATCH',
      payload: input,
      priority: 40,
      maxRetries: 10,
      dependsOn: input.visitLocalId,
      relatedTaskUUID: localId,
      idempotencyKey: localId,
    };
  },

  async sync(row): Promise<SyncResult> {
    const input = row.payload as ReopenVisitInput;
    const urlOrShortCircuit = await resolveCallLogUrl(row, input.visitLocalId);
    if (typeof urlOrShortCircuit !== 'string') return urlOrShortCircuit;
    const url = urlOrShortCircuit;

    let response: Response;
    try {
      response = await fetch(url, {
        method: row.method,
        headers: jsonHeaders(row.idempotencyKey),
        body: JSON.stringify({ edcalltime: null }),
      });
    } catch (err) {
      return networkErrorResult(err);
    }

    const classified = await classifyResponse(response);
    if (classified) return classified;
    return { status: 'success', retriable: false };
  },

  async reconcile() {},

  dependsOn(input) {
    return input.visitLocalId;
  },
};

export type ReopenVisitOp = typeof reopenVisitOp;
